import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useAxios from '../api/axios';

const formatPercentage = (value, total) => {
  const numerator = Number(value || 0);
  const denominator = Number(total || 0);

  if (!denominator) {
    return '0.00';
  }

  return ((numerator / denominator) * 100).toFixed(2);
};

function Stats() {
  const api = useAxios();
  const [stats, setStats] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadStats = async () => {
      try {
        setLoading(true);

        const payload = selectedYear ? { year: Number(selectedYear) } : {};
        const response = await api.post(
          '/getStats',
          JSON.stringify(payload),
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (!isMounted) {
          return;
        }

        const responseYear = Number(response?.data?.year) || new Date().getFullYear();
        setStats(Array.isArray(response?.data?.stats) ? response.data.stats : []);
        setAvailableYears(Array.isArray(response?.data?.availableYears) ? response.data.availableYears : []);

        if (!selectedYear) {
          setSelectedYear(responseYear);
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }

        const message = err?.response?.data?.message || err?.response?.data?.msg;
        toast.error(message || 'Tilastojen haku epäonnistui');
        setStats([]);
        setAvailableYears([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadStats();

    return () => {
      isMounted = false;
    };
  }, [selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto max-w-6xl px-4 pb-10 pt-24">
      <div className='mb-6 rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur md:flex md:items-end md:justify-between'>
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Tilastot</p>
          <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">Kaikkien pelaajien vuositilastot</h1>
          <p className="mt-2 text-sm text-slate-300">Järjestys perustuu voittoprosenttiin: voitetut väylät / pelatut väylät.</p>
        </div>

        <div className="mt-4 md:mt-0">
          <label className="block text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300" htmlFor="stats-year">
            Vuosi
          </label>
          <select
            id="stats-year"
            value={selectedYear ?? ''}
            onChange={(event) => setSelectedYear(Number(event.target.value))}
            className="mt-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white outline-none transition focus:border-cyan-300"
            disabled={loading || availableYears.length === 0}
          >
            {availableYears.length === 0 ? <option value="">Ei vuosia</option> : null}
            {availableYears.map((year) => (
              <option key={year} value={year} className="bg-slate-900 text-white">
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className='rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300'>
          <p className="text-lg font-semibold text-white">Ladataan tilastoja...</p>
        </div>
      ) : stats.length === 0 ? (
        <div className='rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300'>
          <p className="text-lg font-semibold text-white">Tilastoja ei ole vielä saatavilla.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[28px] border border-white/10 bg-slate-900/70 shadow-2xl shadow-slate-950/30 backdrop-blur">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-slate-200">
              <thead className="bg-white/5 text-xs uppercase tracking-[0.2em] text-cyan-300">
                <tr>
                  <th rowSpan="2" title="Sijoitus voittoprosentin mukaan" className="px-4 py-4 align-middle">#</th>
                  <th rowSpan="2" title="Pelaajan nimi" className="px-4 py-4 align-middle">Pelaaja</th>
                  <th rowSpan="2" title="Voittoprosentti lasketaan kaavalla voitetut väylät / pelatut väylät" className="px-4 py-4 align-middle">Voitto%</th>
                  <th rowSpan="2" title="Tappioton prosentti lasketaan kaavalla (voitetut väylät + tasapelit) / pelatut väylät" className="px-4 py-4 align-middle">Tappioton%</th>
                  <th colSpan="2" title="Kilpailukohtaiset tilastot. Sama competitionID kuuluu samaan kisaan." className="border-l border-white/10 bg-cyan-400/10 px-4 py-4 text-center">Kisat</th>
                  <th colSpan="2" title="Kierroskohtaiset tilastot eli vahvistetut ottelut" className="border-l border-white/10 bg-emerald-400/10 px-4 py-4 text-center">Kierrokset</th>
                  <th colSpan="4" title="Väyläkohtaiset tulokset kaikista vahvistetuista kierroksista" className="border-l border-white/10 bg-amber-300/10 px-4 py-4 text-center">Väylät</th>
                </tr>
                <tr>
                  <th title="Pelatut kisat yhteensä kyseisenä vuonna" className="border-l border-white/10 bg-cyan-400/5 px-4 py-4">Yht.</th>
                  <th title="Voitetut kisat eli kilpailun kokonaisvoitot" className="bg-cyan-400/5 px-4 py-4">Voit.</th>
                  <th title="Pelatut kierrokset yhteensä" className="border-l border-white/10 bg-emerald-400/5 px-4 py-4">Yht.</th>
                  <th title="Voitetut kierrokset yhteensä" className="bg-emerald-400/5 px-4 py-4">Voit.</th>
                  <th title="Pelatut väylät yhteensä" className="border-l border-white/10 bg-amber-300/5 px-4 py-4">Yht.</th>
                  <th title="Voitetut väylät yhteensä" className="bg-amber-300/5 px-4 py-4">Voit.</th>
                  <th title="Hävityt väylät yhteensä" className="bg-amber-300/5 px-4 py-4">Häv.</th>
                  <th title="Tasatulokseen päättyneet väylät yhteensä" className="bg-amber-300/5 px-4 py-4">Tas.</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((row, index) => (
                  <tr key={`${row.userID}-${row.year}`} className="border-t border-white/10">
                    <td className="px-4 py-4 font-black text-white">{index + 1}</td>
                    <td className="px-4 py-4 font-semibold text-white">{row.name}</td>
                    <td className="px-4 py-4 font-black text-emerald-300">{Number(row.winPercentage || 0).toFixed(2)} %</td>
                    <td className="px-4 py-4 font-black text-cyan-300">{formatPercentage(Number(row.wonHoles || 0) + Number(row.drawHoles || 0), row.playedHoles)} %</td>
                    <td className="border-l border-white/10 bg-cyan-400/5 px-4 py-4">{row.playedCompetitions ?? row.playedMatches}</td>
                    <td className="bg-cyan-400/5 px-4 py-4">{row.wonCompetitions}</td>
                    <td className="border-l border-white/10 bg-emerald-400/5 px-4 py-4">{row.playedRounds}</td>
                    <td className="bg-emerald-400/5 px-4 py-4">{row.wonRounds}</td>
                    <td className="border-l border-white/10 bg-amber-300/5 px-4 py-4">{row.playedHoles}</td>
                    <td className="bg-amber-300/5 px-4 py-4">{row.wonHoles}</td>
                    <td className="bg-amber-300/5 px-4 py-4">{row.lostHoles}</td>
                    <td className="bg-amber-300/5 px-4 py-4">{row.drawHoles}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Stats;